import { useCallback, useMemo } from "react";

import {
  IconButton,
  OrderableList,
  PrefixChip,
  PropertiesPanelSection,
  SkiliticsIcon,
  Tooltip,
} from "@skilitics/designer-ui-components";
import { useDynamicFormatString, useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { useSelectedAspect, useSelectedResponseId, useSetSelectedResponseId } from "../../selection/useSelectedAspect";
import { HandlerDesignerContextProvider } from "../HandlerDesignerContextProvider";
import { ResponsesDesigner } from "../response-editor/ResponsesDesigner";
import { useTriggerDesigns } from "../TriggerDesignsContext";
import { TriggerListItem } from "./TriggerListItem";
import type { TriggerWithDefinition } from "./TriggersDesigner";
import { useNodeTriggerConditionProp } from "./useNodeTriggerConditionProp";

interface Props {
  nodeId: number;
  triggerWithDefinition: TriggerWithDefinition;
}

const useManagedRemoveHandler = () => {
  const [selectedAspect, setSelectedAspect] = useSelectedAspect();
  const selectedResponseId = useSelectedResponseId();
  const setSelectedResponseId = useSetSelectedResponseId();
  const { getResponses, removeHandler, getHandlers, removeTrigger } = useTriggerDesigns();

  return useCallback(
    (handlerId: number, triggerId: number) => {
      if (selectedAspect === "triggers" && selectedResponseId) {
        if (getResponses(handlerId).find((r) => r.id === selectedResponseId)) {
          setSelectedAspect("triggers");
          setSelectedResponseId();
        }
      }
      const handlers = getHandlers(triggerId);
      if (handlers.length <= 1) {
        removeTrigger(triggerId);
      } else {
        removeHandler(handlerId);
      }
    },
    [
      getResponses,
      removeHandler,
      getHandlers,
      removeTrigger,
      selectedAspect,
      selectedResponseId,
      setSelectedAspect,
      setSelectedResponseId,
    ],
  );
};

export const TriggerSection = ({ nodeId, triggerWithDefinition }: Props) => {
  const { trigger, definition } = triggerWithDefinition;
  const { type, title, description } = definition;
  const { addHandler, getHandlers, reorderHandler, updateTrigger, getTriggersForEventType } = useTriggerDesigns();
  const formatMessage = useFormatMessage(formatStrings);
  const formatCustom = useDynamicFormatString();
  const nodeTriggerConditionProp = useNodeTriggerConditionProp(nodeId);

  const handleAddHandler = () => addHandler(trigger.id);

  const handleOrderChange = useCallback(
    (key: string | number, newIndex: number) => reorderHandler(Number(key), newIndex),
    [reorderHandler],
  );

  const managedRemoveHandler = useManagedRemoveHandler();

  const removeHandler = useCallback(
    (handlerId: number, triggerId: number) => {
      const handlers = getHandlers(triggerId);
      if (handlers.length <= 1) {
        const onTriggerCaseRemove = definition.whereConditionBuilder?.onTriggerCaseRemove;
        if (onTriggerCaseRemove) {
          const allTriggersForEventType = getTriggersForEventType(nodeId, type);
          const otherConfigs = allTriggersForEventType
            .filter((t) => t.id !== trigger.id)
            .flatMap((t) => t.cases.filter((c) => c.eventType === type).map((c) => c.where?.config ?? {}));
          for (const triggerCase of trigger.cases) {
            const config = triggerCase.where?.config ?? {};
            onTriggerCaseRemove({ config, otherConfigs, node: nodeTriggerConditionProp });
          }
        }
      }
      managedRemoveHandler(handlerId, triggerId);
    },
    [
      managedRemoveHandler,
      getHandlers,
      definition,
      trigger,
      nodeId,
      type,
      nodeTriggerConditionProp,
      getTriggersForEventType,
    ],
  );

  const handleWhereConfigChange = useCallback(
    (newConfig: Record<string, unknown>) => {
      const updatedCases = trigger.cases.map((c, i) => (i === 0 ? { ...c, where: { config: newConfig } } : c));
      updateTrigger(trigger.id, { cases: updatedCases, label: trigger.label });
    },
    [trigger, updateTrigger],
  );

  const Configurer = definition.whereConditionBuilder?.configurer;

  const otherConfigs = useMemo(() => {
    const allTriggersForEventType = getTriggersForEventType(nodeId, type);
    return allTriggersForEventType
      .filter((t) => t.id !== trigger.id)
      .flatMap((t) => t.cases.filter((c) => c.eventType === type).map((c) => c.where?.config ?? {}));
  }, [getTriggersForEventType, nodeId, type, trigger.id]);

  const handlers = useMemo(() => getHandlers(trigger.id), [getHandlers, trigger.id]);

  const orderByKey = useMemo(() => handlers.map(({ id }) => `${id}`), [handlers]);

  return (
    <PropertiesPanelSection
      empty
      style={{ paddingBottom: 0 }}
      title={
        <Tooltip title={description ? formatCustom(description) : ""} placement="left">
          <div>
            {Configurer ? (
              <Configurer
                config={trigger.cases[0]?.where?.config ?? {}}
                otherConfigs={otherConfigs}
                onConfigChange={handleWhereConfigChange}
                node={nodeTriggerConditionProp}
              />
            ) : (
              <PrefixChip
                type="tertiary"
                label={
                  <>
                    {formatCustom(title)} {description && <SkiliticsIcon icon="info" />}
                  </>
                }
                data-test="triggers-event-type-heading"
              />
            )}
          </div>
        </Tooltip>
      }
      controls={
        <Tooltip title={formatMessage("addTrigger", { title: formatCustom(title) })} placement="top">
          <IconButton
            icon="plus"
            onClick={handleAddHandler}
            disabled={definition.singleHandler && handlers.length >= 1}
            data-test="add-trigger"
          />
        </Tooltip>
      }
      data-test="trigger-content-container"
    >
      <OrderableList orderByKey={orderByKey} onOrderChange={handleOrderChange}>
        {handlers.map((handler, index) => (
          <HandlerDesignerContextProvider handlerId={handler.id} triggerId={trigger.id} key={handler.id}>
            <TriggerListItem
              isDraggable={handlers.length > 1}
              handler={handler}
              onRemove={() => removeHandler(handler.id, trigger.id)}
              prefixLabel={
                handlers.length > 1
                  ? formatMessage(index === 0 ? "first" : index + 1 === handlers.length ? "last" : "middle")
                  : undefined
              }
            >
              <ResponsesDesigner />
            </TriggerListItem>
          </HandlerDesignerContextProvider>
        ))}
      </OrderableList>
    </PropertiesPanelSection>
  );
};

const formatStrings = {
  addTrigger: {
    en: "Add Trigger to {title}",
  },
  first: {
    en: "First",
  },
  middle: {
    en: "Then",
  },
  last: {
    en: "Lastly",
  },
} satisfies FormatMessages;
